import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { IS_AUTHENTICATED_URL, PLATFORM_IS_AUTHENTICATED_URL } from "../features/UrlHelper";
import { getAuthToken, setAuthToken, clearAuthToken, setKosik } from "../features/tenantStorage";
import { TENANT_MISMATCH_EVENT, SESSION_EXPIRED_EVENT } from "../features/authEvents";
import { useTenant } from "./TenantContext";

const AuthContext = createContext(null);

const ODHLASENY = {
  isLoggedIn: false,
  isAdmin: false,
  userEmail: null,
  userPhone: null,
  userId: null,
  userRole: null,
  userName: null,
  userSurname: null,
  companyId: null,
  companySlug: null,
  actingAs: false,
  isPlatform: false,
};

/** Odpoved loginu / is-authenticated -> stav. Tvar je zhodny pre oba. */
const zOdpovede = (data) => ({
  isLoggedIn: true,
  isAdmin: data.admin === true,
  userEmail: data.email ?? null,
  userPhone: data.phone ?? null,
  userId: data.id ?? null,
  userRole: data.role ?? null,
  userName: data.name ?? null,
  userSurname: data.surname ?? null,
  companyId: data.companyId ?? null,
  companySlug: data.companySlug ?? null,
  actingAs: data.actingAs === true,
  isPlatform: data.platform === true,
});

/** Cas vyprsania tokenu v ms (z `exp` v payloade), alebo null. Podpis sa tu
 *  neoveruje -- to robi server; toto sluzi len na vcasne odhlasenie v UI. */
function expiraciaTokenu(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

// setTimeout pretecie nad 2^31-1 ms (~24,8 dna) a spusti sa hned. 30-dnovy
// token zakaznika sa preto casovacom nestrazi -- zachyti ho 401 od servera
// alebo kontrola pri navrate na kartu.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

export function AuthProvider({ children }) {
  const { mode, tenant } = useTenant();
  const [stav, setStav] = useState(ODHLASENY);
  const [authLoading, setAuthLoading] = useState(true);

  /**
   * Token patri inej prevadzke, nez je v URL? (napr. prihlasenie v /demo
   * a prechod na /veteran v tej istej zalozke). Backend by aj tak vratil data
   * firmy z tokenu, nie z URL -- takze je to cisto UX, unik dat je serverovo
   * nemozny. Riesenie je TICHE odhlasenie, ziadny redirect (riziko slucky).
   */
  const jeCudziToken = useCallback(
    (data) => {
      if (mode === "platform") return data.platform !== true;
      if (mode === "path" || mode === "host") return !tenant || data.companyId !== tenant.id;
      return true; // root (marketing) prihlasenie nema
    },
    [mode, tenant],
  );

  /**
   * @param {object} data  odpoved /api/login, /api/verify-phone,
   *                       /api/change-initial-password alebo /api/platform/login
   */
  const login = useCallback((data) => {
    // O trvalom ulozeni rozhoduje SERVER cez `persistent` (R18) -- dlhy token
    // dostane len zakaznik. Keby o tom rozhodoval klient, staci si priznak
    // poslat rucne a obsluha by mala 30-dnovu session na spolocnom pocitaci.
    if (data?.token) setAuthToken(data.token, data.persistent === true);
    setStav(zOdpovede(data || {}));
  }, []);

  const logout = useCallback(() => {
    clearAuthToken();
    // Kosik je v relacnom ulozisku karty a odhlasenim by prezil -- na
    // spolocnom pocitaci by rozpracovanu objednavku (jedla, poznamku) videl
    // dalsi clovek. Pri vyprsani prihlasenia sa zamerne NEMAZE: ten isty
    // clovek sa prihlasi znova a pokracuje.
    setKosik(null);
    setStav(ODHLASENY);
  }, []);

  const checkAuthStatus = useCallback(async () => {
    const token = getAuthToken();

    // Ak nemáš token (alebo si na marketingu), neskontroluj autentifikáciu
    if (!token || mode === "root") {
      setStav(ODHLASENY);
      setAuthLoading(false);
      return;
    }

    const url = mode === "platform" ? PLATFORM_IS_AUTHENTICATED_URL : IS_AUTHENTICATED_URL;
    await axios
      .get(url)
      .then((response) => {
        if (jeCudziToken(response.data)) {
          clearAuthToken();
          setStav(ODHLASENY);
          return;
        }
        setStav(zOdpovede(response.data));
      })
      .catch(() => {
        setStav(ODHLASENY);
      })
      .finally(() => {
        setAuthLoading(false);
      });
  }, [mode, jeCudziToken]);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  // Backend odmietol token ako token inej prevadzky (axiosInterceptor ho uz
  // zahodil) -- zosuladit stav, inak by UI tvrdilo, ze je pouzivatel prihlaseny.
  useEffect(() => {
    const onMismatch = () => setStav(ODHLASENY);
    window.addEventListener(TENANT_MISMATCH_EVENT, onMismatch);
    return () => window.removeEventListener(TENANT_MISMATCH_EVENT, onMismatch);
  }, []);

  // Prihlasenie vyprsalo (interceptor alebo casovac nizsie token uz zahodil).
  // Act-as sa vracia do /sprava: platformovy token tam ostal vo vlastnom kluci
  // a spravca nema byt v cudzej prevadzke odhlaseny s pruhom, ktory klame.
  const actingAsRef = useRef(false);
  actingAsRef.current = stav.actingAs;
  useEffect(() => {
    const onExpired = () => {
      if (actingAsRef.current) {
        window.location.assign("/sprava");
        return;
      }
      setStav(ODHLASENY);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  // Odhlasenie v okamihu vyprsania, nie az pri dalsej poziadavke -- inak by
  // pruh act-as po hodine svietil dalej a zakaznik by videl verejne ceny
  // (verejne endpointy vyprsany token ticho ignoruju). Kontrola aj pri navrate
  // na kartu: casovac v uspanom notebooku ci karte na pozadi mesk.
  useEffect(() => {
    if (!stav.isLoggedIn) return undefined;

    const skontroluj = () => {
      const exp = expiraciaTokenu(getAuthToken());
      if (exp && Date.now() >= exp) {
        clearAuthToken();
        window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      }
    };

    const exp = expiraciaTokenu(getAuthToken());
    const zostava = exp ? exp - Date.now() : null;
    const casovac = zostava !== null && zostava < MAX_TIMEOUT_MS
      ? setTimeout(skontroluj, Math.max(zostava, 0) + 1000)
      : null;
    const priNavrate = () => { if (document.visibilityState !== "hidden") skontroluj(); };

    window.addEventListener("focus", skontroluj);
    document.addEventListener("visibilitychange", priNavrate);
    return () => {
      if (casovac) clearTimeout(casovac);
      window.removeEventListener("focus", skontroluj);
      document.removeEventListener("visibilitychange", priNavrate);
    };
  }, [stav.isLoggedIn, stav.userId]);

  return (
    <AuthContext.Provider value={{ ...stav, authLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
